import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, Chip, Card, CardContent,
  LinearProgress, CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import EnhancedFilesTable from './EnhancedFilesTable';

interface ZendeskConfigState {
  subdomain: string;
  email: string;
  apiToken: string;
  isConfigured: boolean;
  totalArticles: number;
  lastSync: string | null;
  zendeskCount: number;
}

const ZendeskConfig: React.FC = () => {
  const [config, setConfig] = useState<ZendeskConfigState>({
    subdomain: '', email: '', apiToken: '', isConfigured: false,
    totalArticles: 0, lastSync: null, zendeskCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'none' | 'success' | 'error'>('none');
  const [syncResult, setSyncResult] = useState<any>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [polling, setPolling] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any>(null);
  const progressInterval = useRef<any>(null);

  useEffect(() => {
    loadConfiguration();
    loadUploadedDocuments();
  }, []);

  useEffect(() => {
    let docPollInterval: any;
    if (polling) {
      docPollInterval = setInterval(() => { loadUploadedDocuments(); }, 2000);
    }
    return () => { if (docPollInterval) clearInterval(docPollInterval); };
  }, [polling]);

  const pollJobProgress = useCallback(async (jobId: string) => {
    if (progressInterval.current) clearInterval(progressInterval.current);
    progressInterval.current = setInterval(async () => {
      try {
        const res = await apiService.get(`/api/v1/sources/zendesk/job/${jobId}`);
        const jobData = res.data;
        setJobProgress(jobData.progress);
        if (jobData.status === 'completed' || jobData.status === 'failed') {
          clearInterval(progressInterval.current);
          progressInterval.current = null;
          setSyncing(false);
          setCurrentJobId(null);
          if (jobData.status === 'completed') {
            setSyncResult(jobData.result);
            setMessage({
              type: 'success',
              text: `Sync completed! Added ${jobData.result?.added || 0}, updated ${jobData.result?.updated || 0}, skipped ${jobData.result?.skipped || 0}`,
            });
          } else {
            setMessage({ type: 'error', text: `Sync failed: ${jobData.error_message || 'Unknown error'}` });
          }
          loadConfiguration();
          loadUploadedDocuments();
        }
      } catch (err) {
        console.error('Error polling job:', err);
      }
    }, 2000);
  }, []);

  useEffect(() => {
    return () => { if (progressInterval.current) clearInterval(progressInterval.current); };
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const res = await apiService.get('/api/v1/sources/zendesk/config');
      const cfg = res.data;

      let zendeskCount = 0;
      try {
        const statsRes = await apiService.getDocumentStats();
        zendeskCount = statsRes?.by_source?.zendesk || 0;
      } catch { /* ignore */ }

      let lastSync: string | null = null;
      try {
        const jobsRes = await apiService.get('/api/v1/sources/zendesk/jobs?limit=1');
        const jobs = jobsRes.data?.jobs || [];
        const lastCompleted = jobs.find((j: any) => j.status === 'completed');
        if (lastCompleted?.completed_at) lastSync = lastCompleted.completed_at;
      } catch { /* ignore */ }

      setConfig({
        subdomain: cfg.subdomain || '',
        email: cfg.email || '',
        apiToken: cfg.api_token === '***' ? '••••••••' : cfg.api_token || '',
        isConfigured: cfg.enabled || false,
        totalArticles: 0,
        lastSync,
        zendeskCount,
      });

      if (cfg.enabled) setConnectionStatus('success');
    } catch (err) {
      console.error('Error loading config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const payload: any = { subdomain: config.subdomain, email: config.email };
      if (config.apiToken && config.apiToken !== '••••••••') payload.api_token = config.apiToken;
      const res = await apiService.post('/api/v1/sources/zendesk/test', payload);
      const result = res.data;
      if (result.status === 'success') {
        setConnectionStatus('success');
        setConfig(prev => ({ ...prev, totalArticles: result.total_articles || 0 }));
        setMessage({ type: 'success', text: result.message });
      } else {
        setConnectionStatus('error');
        setMessage({ type: 'error', text: result.message });
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConfig = async () => {
    setMessage(null);
    try {
      const payload: any = { subdomain: config.subdomain, email: config.email };
      if (config.apiToken && config.apiToken !== '••••••••') payload.api_token = config.apiToken;
      await apiService.post('/api/v1/sources/zendesk/config', payload);
      setMessage({ type: 'success', text: 'Configuration saved successfully' });
      setConfig(prev => ({ ...prev, isConfigured: true }));
      setConnectionStatus('success');
      loadConfiguration();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save configuration' });
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setJobProgress(null);
    setMessage(null);
    try {
      const res = await apiService.post('/api/v1/sources/zendesk/sync', {});
      const data = res.data;
      if (data.job_id) {
        setCurrentJobId(data.job_id);
        setMessage({ type: 'info', text: 'Zendesk sync started...' });
        pollJobProgress(data.job_id);
      }
    } catch (err: any) {
      setSyncing(false);
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to start sync' });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadResult(null);
    setMessage(null);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
      formData.append('source_type', 'zendesk');
      const res = await apiService.postForm('/api/v1/documents/upload', formData);
      setUploadResult(res.data);
      setMessage({ type: 'success', text: `Uploaded ${files.length} file(s) successfully` });
      loadUploadedDocuments();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const loadUploadedDocuments = async () => {
    try {
      const res = await apiService.getDocuments({ page: 1, per_page: 25, source_type: 'zendesk' });
      setUploadedDocs(res.documents || []);
      setTotalDocs(res.total || 0);
      const hasPending = (res.documents || []).some(
        (d: any) => d.status === 'pending' || d.status === 'processing'
      );
      setPolling(hasPending);
    } catch { /* ignore */ }
  };

  const handleLoadMoreDocuments = async (page: number, perPage: number, search?: string) => {
    const res = await apiService.getDocuments({ page, per_page: perPage, source_type: 'zendesk', search });
    return { documents: res.documents || [], total: res.total || 0 };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 1, md: 2 }, py: 2, overflowX: 'hidden', boxSizing: 'border-box' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, fontFamily: '"Outfit", sans-serif' }}>
        Zendesk Help Center Integration
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Connect to Zendesk Help Center to sync support articles into BurnGPT's knowledge base.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {/* API Configuration */}
        <Card sx={{ flex: '1 1 400px', minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>API Configuration</Typography>
            <TextField
              fullWidth label="Zendesk Subdomain" size="small" sx={{ mb: 2 }}
              value={config.subdomain}
              onChange={(e) => setConfig(prev => ({ ...prev, subdomain: e.target.value }))}
              placeholder="burnbootcamp"
              helperText="The subdomain from your Zendesk URL (e.g. burnbootcamp.zendesk.com)"
            />
            <TextField
              fullWidth label="Email Address" size="small" sx={{ mb: 2 }}
              value={config.email}
              onChange={(e) => setConfig(prev => ({ ...prev, email: e.target.value }))}
              placeholder="admin@example.com"
              helperText="The email tied to the API token"
            />
            <TextField
              fullWidth label="API Token" size="small" type="password" sx={{ mb: 2 }}
              value={config.apiToken}
              onChange={(e) => setConfig(prev => ({ ...prev, apiToken: e.target.value }))}
              placeholder="Enter API token"
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined" onClick={handleTestConnection} disabled={testing || !config.subdomain || !config.email}
                startIcon={testing ? <CircularProgress size={16} /> : undefined}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button
                variant="contained" onClick={handleSaveConfig}
                disabled={!config.subdomain || !config.email || connectionStatus !== 'success'}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Status */}
        <Card sx={{ flex: '1 1 280px', minWidth: 250 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Current Status</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Connection:</Typography>
                {config.isConfigured ? (
                  <Chip label="Connected" size="small" color="success" icon={<CheckCircleIcon />} />
                ) : (
                  <Chip label="Not Configured" size="small" color="default" icon={<ErrorIcon />} />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Articles in KB:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{config.zendeskCount}</Typography>
              </Box>
              {config.lastSync && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Last Sync:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {new Date(config.lastSync).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Article Management */}
      {config.isConfigured && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Article Management</Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* Sync */}
              <Paper variant="outlined" sx={{ flex: '1 1 300px', p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Zendesk Sync</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Sync all published Help Center articles with embeddings for AI search.
                </Typography>
                <Button
                  variant="contained" onClick={handleSync} disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                  fullWidth
                >
                  {syncing ? 'Syncing...' : 'Sync Articles Now'}
                </Button>
                {syncing && jobProgress && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">{jobProgress.message || 'Processing...'}</Typography>
                      <Typography variant="caption">{jobProgress.percentage || 0}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate" value={jobProgress.percentage || 0}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    {jobProgress.details && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={`Added: ${jobProgress.details.added || 0}`} size="small" color="success" variant="outlined" />
                        <Chip label={`Updated: ${jobProgress.details.updated || 0}`} size="small" color="info" variant="outlined" />
                        <Chip label={`Skipped: ${jobProgress.details.skipped || 0}`} size="small" variant="outlined" />
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>

              {/* Manual Upload */}
              <Paper variant="outlined" sx={{ flex: '1 1 300px', p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Manual Upload</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Upload additional documents to supplement Zendesk articles.
                </Typography>
                <input
                  type="file" multiple accept=".pdf,.docx,.txt,.pptx,.xlsx,.csv"
                  style={{ display: 'none' }} id="zendesk-upload-input"
                  onChange={handleFileUpload}
                />
                <label htmlFor="zendesk-upload-input">
                  <Button
                    variant="outlined" component="span" disabled={uploading} fullWidth
                    startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                  >
                    {uploading ? 'Uploading...' : 'Upload Documents'}
                  </Button>
                </label>
              </Paper>
            </Box>

            {/* Sync Results */}
            {syncResult && (
              <Paper variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Sync Results</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Added: ${syncResult.added || 0}`} size="small" color="success" />
                  <Chip label={`Updated: ${syncResult.updated || 0}`} size="small" color="info" />
                  <Chip label={`Skipped: ${syncResult.skipped || 0}`} size="small" />
                  <Chip label={`Failed: ${syncResult.failed || 0}`} size="small" color="error" />
                  <Chip label={`Total: ${syncResult.total_articles || 0}`} size="small" variant="outlined" />
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      )}

      {/* Articles Table */}
      <EnhancedFilesTable
        files={uploadedDocs}
        title="Zendesk Articles"
        onLoadMore={handleLoadMoreDocuments}
        totalCount={totalDocs}
        serverSidePagination={true}
      />
    </Box>
  );
};

export default ZendeskConfig;
